import React, { useState, useEffect } from 'react';
import { myChartApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Link2, RefreshCw, Users, Activity, Pill, Heart,
  Loader2, CheckCircle, Settings, ArrowUpDown,
  Cloud, Database, Shield, AlertTriangle, Stethoscope, FileText
} from 'lucide-react';

export default function MyChartIntegrationPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showFhir, setShowFhir] = useState(null);
  const [fhirData, setFhirData] = useState(null);
  const [pushing, setPushing] = useState(null);
  const [config, setConfig] = useState({ epic_base_url: '', client_id: '', client_secret: '', fhir_endpoint: '' });

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await myChartApi.status();
      setStatus(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadPatients = async () => {
    setPatientsLoading(true);
    try {
      const res = await myChartApi.patients();
      setPatients(res.data.patients || []);
    } catch (err) { console.error(err); }
    finally { setPatientsLoading(false); }
  };

  useEffect(() => { loadStatus(); }, []);
  useEffect(() => { if (status?.connected || status?.demo_mode) loadPatients(); }, [status]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await myChartApi.sync();
      toast.success(`Synced: ${res.data.stats?.patients_synced || 0} patients`);
      loadStatus();
      loadPatients();
    } catch (err) { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  const handleConfigure = async () => {
    try {
      await myChartApi.configure(config);
      toast.success('MyChart configured');
      setShowConfig(false);
      loadStatus();
    } catch (err) { toast.error('Configuration failed'); }
  };

  const handlePush = async (patientId) => {
    setPushing(patientId);
    try {
      const res = await myChartApi.push(patientId);
      toast.success(`Pushed ${res.data.resources_pushed?.length || 0} resources to MyChart`);
    } catch (err) { toast.error('Push failed'); }
    finally { setPushing(null); }
  };

  const viewFhir = async (resourceType, patientId) => {
    setShowFhir({ type: resourceType, patientId });
    try {
      const res = await myChartApi.fhirResource(resourceType, patientId);
      setFhirData(res.data);
    } catch (err) { toast.error('Failed to load FHIR data'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>;

  const ss = status?.sync_stats || {};

  return (
    <div className="space-y-6" data-testid="mychart-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>MyChart Integration</h1>
          <p className="text-[#64748B] mt-1">Connect to Epic MyChart via FHIR R4 for patient data sync</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowConfig(true)} data-testid="configure-mychart-btn"><Settings className="w-4 h-4 mr-2" />Configure</Button>
          <Button onClick={handleSync} disabled={syncing} className="careiq-btn-primary" data-testid="sync-mychart-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        </div>
      </div>

      {/* Connection Status */}
      <Card className="careiq-card" data-testid="mychart-status">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${status?.demo_mode ? 'bg-[#92400E]/10' : status?.connected ? 'bg-[#166534]/10' : 'bg-[#991B1B]/10'}`}>
              {status?.demo_mode ? <Database className="w-7 h-7 text-[#92400E]" /> : status?.connected ? <Cloud className="w-7 h-7 text-[#166534]" /> : <AlertTriangle className="w-7 h-7 text-[#991B1B]" />}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className={`text-lg font-semibold ${status?.demo_mode ? 'text-[#92400E]' : status?.connected ? 'text-[#166534]' : 'text-[#991B1B]'}`}>
                  {status?.demo_mode ? 'Demo Mode' : status?.connected ? 'Connected' : 'Not Connected'}
                </span>
                {status?.demo_mode && <span className="text-[10px] px-2 py-0.5 bg-[#92400E]/10 text-[#92400E] rounded-full font-medium">SIMULATED DATA</span>}
              </div>
              <p className="text-sm text-[#64748B]">
                {status?.demo_mode ? 'Using simulated MyChart data. Configure Epic credentials for live connection.' : status?.connected ? `FHIR Endpoint: ${status.fhir_endpoint}` : 'Configure your Epic App Orchard credentials to connect.'}
              </p>
              {status?.last_sync && <p className="text-xs text-[#94A3B8] mt-1">Last sync: {new Date(status.last_sync).toLocaleString()}</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sync Stats */}
      {Object.keys(ss).length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="sync-stats">
          {[
            { label: 'Patients', value: ss.patients_synced, icon: Users, color: '#0E7490' },
            { label: 'Vitals', value: ss.vitals_synced, icon: Activity, color: '#DC2626' },
            { label: 'Medications', value: ss.medications_synced, icon: Pill, color: '#7C3AED' },
            { label: 'Lab Results', value: ss.lab_results_synced, icon: Stethoscope, color: '#166534' },
          ].map((stat, i) => (
            <Card key={i} className="careiq-metric-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: stat.color + '15' }}>
                    <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#0A2342]">{stat.value || 0}</div>
                    <div className="text-xs text-[#64748B]">{stat.label} Synced</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Synced Patients */}
      <Card className="careiq-card" data-testid="mychart-patients">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
            <Users className="w-5 h-5 text-[#0E7490]" />
            Synced Patients ({patients.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {patientsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#0E7490] animate-spin" /></div>
          ) : patients.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0]">
                    <th className="table-header text-left">Patient</th>
                    <th className="table-header text-left">MyChart ID</th>
                    <th className="table-header text-left">MRN</th>
                    <th className="table-header text-left">Last Vitals</th>
                    <th className="table-header text-left">Meds</th>
                    <th className="table-header text-left">Sync</th>
                    <th className="table-header text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {patients.map((p) => (
                    <tr key={p.id} className="table-row" data-testid={`mychart-patient-${p.id}`}>
                      <td className="table-cell">
                        <div className="font-medium text-[#0A2342]">{p.name}</div>
                        <div className="text-xs text-[#64748B]">{p.gender} &middot; DOB: {p.dob}</div>
                      </td>
                      <td className="table-cell font-mono text-xs text-[#0E7490]">{p.mychart_id}</td>
                      <td className="table-cell text-sm text-[#64748B]">{p.mrn}</td>
                      <td className="table-cell text-xs">
                        {p.last_vitals?.bp ? (
                          <div>
                            <span className="text-[#0A2342]">BP: {p.last_vitals.bp}</span>
                            {p.last_vitals.hr && <span className="text-[#64748B] ml-2">HR: {p.last_vitals.hr}</span>}
                          </div>
                        ) : <span className="text-[#94A3B8]">-</span>}
                      </td>
                      <td className="table-cell text-sm text-[#0A2342]">{p.active_medications}</td>
                      <td className="table-cell">
                        <span className="flex items-center gap-1 text-xs text-[#166534]"><CheckCircle className="w-3 h-3" />Synced</span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => viewFhir('Observation', p.id)} data-testid={`view-fhir-${p.id}`}>
                            <FileText className="w-3 h-3 mr-1" />FHIR
                          </Button>
                          <Button size="sm" variant="outline" className="text-xs text-[#0E7490]" onClick={() => handlePush(p.id)} disabled={pushing === p.id} data-testid={`push-mychart-${p.id}`}>
                            <ArrowUpDown className="w-3 h-3 mr-1" />
                            {pushing === p.id ? '...' : 'Push'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-[#64748B]">
              <Users className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" />
              <p>No patients synced yet. Click "Sync Now" to pull data.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* FHIR Resource Viewer */}
      <Dialog open={!!showFhir} onOpenChange={() => { setShowFhir(null); setFhirData(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh]" data-testid="fhir-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#0E7490]" />
              FHIR R4 Resource: {showFhir?.type}
            </DialogTitle>
            <p className="text-sm text-[#64748B]">Epic MyChart FHIR data (demo mode)</p>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {fhirData ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-[#64748B]">
                  <span>Type: {fhirData.resourceType}</span>
                  <span>&middot; Total: {fhirData.total}</span>
                  <span>&middot; Source: {fhirData.source}</span>
                </div>
                <pre className="p-4 bg-[#0A2342] text-[#E2E8F0] rounded-lg text-xs overflow-auto max-h-[400px] font-mono">
                  {JSON.stringify(fhirData, null, 2)}
                </pre>
              </div>
            ) : (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#0E7490] animate-spin" /></div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Configuration Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="sm:max-w-md" data-testid="mychart-config-dialog">
          <DialogHeader>
            <DialogTitle>Configure Epic MyChart</DialogTitle>
            <p className="text-sm text-[#64748B]">Enter your Epic App Orchard credentials. Leave blank for demo mode.</p>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Epic Base URL</Label><Input placeholder="https://fhir.epic.com" value={config.epic_base_url} onChange={(e) => setConfig({ ...config, epic_base_url: e.target.value })} /></div>
            <div><Label>FHIR Endpoint</Label><Input placeholder="https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4" value={config.fhir_endpoint} onChange={(e) => setConfig({ ...config, fhir_endpoint: e.target.value })} /></div>
            <div><Label>Client ID</Label><Input placeholder="Your Epic App client ID" value={config.client_id} onChange={(e) => setConfig({ ...config, client_id: e.target.value })} /></div>
            <div><Label>Client Secret</Label><Input type="password" placeholder="Your Epic App client secret" value={config.client_secret} onChange={(e) => setConfig({ ...config, client_secret: e.target.value })} /></div>
            <div className="p-3 bg-[#92400E]/5 rounded-lg text-xs text-[#92400E]">
              <strong>Demo Mode:</strong> If credentials are left blank, the system runs in demo mode with simulated MyChart data. To connect to a real Epic instance, register your app at <a href="https://fhir.epic.com/" target="_blank" rel="noopener noreferrer" className="underline">Epic App Orchard</a>.
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowConfig(false)}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleConfigure} data-testid="save-mychart-config-btn">Save Configuration</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
